// @ts-nocheck
// app/course/[courseId].tsx
// ✅ v10.2 — handleExternalCTA now opens 'official' modal instead of direct URL

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Platform,
  Linking,
  Alert,
  Image,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Animated,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Head from 'expo-router/head';
import {
  ExternalLink,
  Phone,
  MessageCircle,
  Clock,
  DollarSign,
  Monitor,
  Globe,
  Award,
  BookOpen,
  CheckCircle,
  Zap,
  Sparkles,
  Gift,
  ChevronRight,
  RefreshCcw,
  Mail,
} from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import { BookCallModal } from '@/components/BookCallModal';
import { trackPageView, trackButtonClick, trackFunnelStep } from '@/utils/analytics';
import { usePageEngagement } from '@/hooks/usePageEngagement';
import { Course } from '@/types/chat';

// ─── Constants ────────────────────────────────────────────────────────────────
const IS_WEB = Platform.OS === 'web';
const SITE_URL = 'https://kursiaii.com';

// Editorial palette (terracotta + cream + ink)
const UI = {
  bg: '#F2EBDD',
  surface: '#FAF5EA',
  text: '#1A1611',
  textLight: '#3A332A',
  muted: '#7A6F5F',
  border: '#1A1611',

  chipBg: 'transparent',
  chipBorder: '#1A1611',

  tintA: '#FAF5EA',
  tintB: '#F2EBDD',
  tintC: '#E8DFCB',
};

const KURSI_LOGO = require('../assets/images/לוגו.png');

// ─── Types ────────────────────────────────────────────────────────────────────
type DBCourse = {
  id: number;
  course_id: string | null;
  source_url_key: string | null;
  title: string | null;
  provider: string | null;
  topic: string | null;
  focus: string | null;
  type: string | null;
  format: string | null;
  level: string | null;
  price: string | null;
  duration: string | null;
  language: string | null;
  summary: string | null;
  source_urls: string | null;
  has_voucher: boolean;
  voucher_text: string | null;
  vouchers_for_kursi_users: string | null;
  audience: string | null;
  outcomes: string | null;
  deliverables: string | null;
  uniqueness: string | null;
  target_skills: string | null;
  images: string | null;
  relevant: string | null;
  lightrag_doc: string | null;
};

type CoursePayload = {
  id?: string;
  name?: string;
  mentor?: string;
  price?: string;
  duration?: string;
  learningType?: string;
  audience?: string;
  outcomes?: string;
  summary?: string;
  imageUrl?: string;
  source_url?: string;
  language?: string;
  type?: string;
  topic?: string;
  subTopic?: string;
  matchReason?: string;
  session_id?: string;
  sessionId?: string;
  relatedCourses?: any[];
  courses?: any[];
  otherCourses?: any[];
  gateCourses?: any[];
};

type RelatedCourse = {
  course_id: string;
  title: string;
  provider: string;
  topic?: string;
  format?: string;
  level?: string;
  language?: string;
  summary?: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function safeStr(v: unknown): string {
  return (v ?? '').toString().trim();
}

function parsePayload(raw: string | string[] | undefined): CoursePayload {
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (!s) return {};
  try {
    return JSON.parse(decodeURIComponent(s));
  } catch {
    if (__DEV__) console.warn('[CourseProxy] parsePayload failed', s.slice(0, 80));
    return {};
  }
}

function parseJsonArray(raw: string | string[] | undefined): any[] {
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (!s) return [];
  try {
    return JSON.parse(decodeURIComponent(s));
  } catch {
    return [];
  }
}

function getFirstUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  const parts = raw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const url = parts[0] ?? '';
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (/^[\w-]+\.[a-zA-Z]{2,6}(\/|$)/.test(url)) return `https://${url}`;
  return '';
}

function truncate(text: string, max: number): string {
  const chars = [...text];
  if (chars.length <= max) return text;
  return chars.slice(0, max - 1).join('') + '…';
}

function splitItems(text: string): string[] {
  if (!text) return [];
  return text
    .split(/[\n\r•·;]+/)
    .map((s) =>
      s
        .replace(/^[-–—*\d.]\s*/, '')
        .replace(/,\s*$/, '')
        .trim()
    )
    .filter((s) => s.length > 3);
}

function buildMetaTitle(title: string, provider: string): string {
  if (!title) return 'קורס — קורסי';
  if (provider) return `${title} | ${provider} — קורסי`;
  return `${title} — קורסי`;
}

function buildMetaDesc(title: string, provider: string, summary: string): string {
  const base = `קורס ${title}${provider ? ` מאת ${provider}` : ''}`;
  if (summary) return truncate(`${base} — ${summary}`, 155);
  return truncate(base, 155);
}

function makeClientId(prefix = 'ps'): string {
  const uid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${uid}`;
}

function getGateAccessKey(courseId: string): string {
  return `@kursi_course_page_gate_v1:${courseId}`;
}

function normalizeRelatedItems(items: any[], currentCourseId: string): RelatedCourse[] {
  const out: RelatedCourse[] = [];
  for (const it of items || []) {
    const course_id = safeStr(it?.course_id || it?.id || it?.courseId || it?.source_url_key);
    const title = safeStr(it?.title || it?.name);
    const provider = safeStr(it?.provider || it?.mentor);
    if (!course_id || !title) continue;
    if (course_id === currentCourseId) continue;
    out.push({
      course_id,
      title,
      provider,
      topic: safeStr(it?.topic),
      format: safeStr(it?.format || it?.learningType),
      level: safeStr(it?.level),
      language: safeStr(it?.language),
      summary: safeStr(it?.summary || it?.description),
    });
  }
  const seen = new Set<string>();
  return out.filter((c) => {
    if (seen.has(c.course_id)) return false;
    seen.add(c.course_id);
    return true;
  });
}

function isMeaningfulTag(v: string): boolean {
  const t = safeStr(v);
  if (!t) return false;
  const norm = t.replace(/\s+/g, ' ').trim();
  if (norm === 'לא צוין' || norm === 'לא צויין' || norm === 'לא ידוע') return false;
  if (norm.toLowerCase() === 'n/a') return false;
  return true;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Tag({ text }: { text: string }) {
  const t = safeStr(text);
  if (!isMeaningfulTag(t)) return null;
  return (
    <View style={s.tag}>
      <Text style={s.tagText}>{t}</Text>
    </View>
  );
}

function FactItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  if (!value) return null;
  return (
    <View style={s.factItem}>
      {icon}
      <View style={s.factTexts}>
        <Text style={s.factLabel}>{label}</Text>
        <Text style={s.factValue}>{value}</Text>
      </View>
    </View>
  );
}

function RelatedCard({ c, index, onPress }: { c: RelatedCourse; index: number; onPress: () => void }) {
  const title = safeStr(c.title);
  const provider = safeStr(c.provider);
  const meta = [safeStr(c.topic), safeStr(c.format), safeStr(c.level)]
    .filter(Boolean)
    .slice(0, 2)
    .join(' · ');

  return (
    <Pressable
      style={s.relatedCard}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`קורס נוסף ${index + 1}: ${title}`}
    >
      <View style={s.relatedCardTop}>
        <Text style={s.relatedTitle} numberOfLines={2}>
          {title}
        </Text>
        <ChevronRight size={16} color={theme.colors.primary} />
      </View>
      {provider ? <Text style={s.relatedProvider} numberOfLines={1}>{provider}</Text> : null}
      {meta ? <Text style={s.relatedMeta} numberOfLines={1}>{meta}</Text> : null}
      {c.summary ? (
        <Text style={s.relatedSummary} numberOfLines={2}>
          {truncate(safeStr(c.summary), 140)}
        </Text>
      ) : null}
    </Pressable>
  );
}

function Section({ index, children }: { index: number; children: React.ReactNode }) {
  const anim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    anim.setValue(0);
    Animated.timing(anim, {
      toValue: 1,
      duration: 380,
      delay: 60 + index * 45,
      useNativeDriver: true,
    }).start();
  }, [anim, index]);

  const translateY = anim.interpolate({ inputRange: [0, 1], outputRange: [10, 0] });
  const bg = index % 3 === 0 ? UI.tintA : index % 3 === 1 ? UI.tintB : UI.tintC;

  return (
    <Animated.View style={[s.sectionCard, { backgroundColor: bg, opacity: anim, transform: [{ translateY }] }]}>
      {children}
    </Animated.View>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CourseProxyPage() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    courseId?: string;
    payload?: string;
    source?: string;
    conversationId?: string;
    relatedCourses?: string;
  }>();

  const rawCourseId = safeStr(params.courseId);
  const payload = useMemo(() => parsePayload(params.payload), [params.payload]);
  const source = safeStr(params.source);
  const conversationId = safeStr(params.conversationId);

  const [dbCourse, setDbCourse] = useState<DBCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'lead' | 'official' | 'newsletter'>('lead');

  const [gateLoaded, setGateLoaded] = useState(false);
  const [gateUnlocked, setGateUnlocked] = useState(false);

  const relatedFromParams = useMemo(() => parseJsonArray(params.relatedCourses), [params.relatedCourses]);
  const relatedFromPayload = useMemo(() => {
    const arr =
      (payload.relatedCourses as any[]) ||
      (payload.courses as any[]) ||
      (payload.otherCourses as any[]) ||
      (payload.gateCourses as any[]) ||
      [];
    return Array.isArray(arr) ? arr : [];
  }, [payload]);

  const relatedFromGate: RelatedCourse[] = useMemo(() => {
    const combined = [...relatedFromParams, ...relatedFromPayload];
    return normalizeRelatedItems(combined, rawCourseId).slice(0, 6);
  }, [relatedFromParams, relatedFromPayload, rawCourseId]);

  const hasRelatedFromGate = relatedFromGate.length > 0;

  const [pageSessionPk, setPageSessionPk] = useState<string>('');
  const pageSessionClientIdRef = useRef<string>(makeClientId('ps'));
  const pendingSessionRef = useRef<Promise<string> | null>(null);

  const relatedSectionLayoutRef = useRef<{ y: number; h: number } | null>(null);
  const relatedSectionViewedRef = useRef(false);
  const lastRelatedScrollLogTsRef = useRef<number>(0);

  const pagePath = `/course/${rawCourseId}`;

  const entry = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    entry.setValue(0);
    Animated.timing(entry, { toValue: 1, duration: 380, useNativeDriver: true }).start();
  }, [entry, rawCourseId]);

  const entryStyle = useMemo(() => {
    const translateY = entry.interpolate({ inputRange: [0, 1], outputRange: [10, 0] });
    return { opacity: entry, transform: [{ translateY }] };
  }, [entry]);

  usePageEngagement(pagePath);

  useEffect(() => {
    let mounted = true;

    const checkGate = async () => {
      if (!rawCourseId) {
        if (!mounted) return;
        setGateUnlocked(false);
        setGateLoaded(true);
        return;
      }

      try {
        const marker = await AsyncStorage.getItem(getGateAccessKey(rawCourseId));
        if (!mounted) return;
        setGateUnlocked(marker === '1');
      } catch {
        if (!mounted) return;
        setGateUnlocked(false);
      } finally {
        if (mounted) setGateLoaded(true);
      }
    };

    setGateLoaded(false);
    void checkGate();

    return () => {
      mounted = false;
    };
  }, [rawCourseId]);

  const fetchCourse = useCallback(
    async (signal: { alive: boolean }) => {
      try {
        setLoading(true);
        setFetchError(null);

        const id = rawCourseId;
        if (!id) { setFetchError('חסר מזהה קורס'); return; }

        const isNumeric = /^[0-9]+$/.test(id);
        const base = supabase
          .from('processed_raw_courses')
          .select(
            'id,course_id,source_url_key,title,provider,topic,focus,type,format,level,price,duration,language,summary,source_urls,has_voucher,voucher_text,vouchers_for_kursi_users,audience,outcomes,deliverables,uniqueness,target_skills,images,relevant,lightrag_doc'
          )
          .is('deleted_at', null)
          .order('id', { ascending: false })
          .limit(1);

        const { data, error } = isNumeric
          ? await base.or(`id.eq.${id},course_id.eq.${id}`)
          : await base.or(`course_id.eq.${id},source_url_key.eq.${id}`);

        if (!signal.alive) return;

        if (error) { setFetchError('שגיאה בטעינת הקורס'); return; }
        setDbCourse(data && data.length > 0 ? (data[0] as DBCourse) : null);
      } catch {
        if (signal.alive) setFetchError('שגיאה בטעינת הקורס');
      } finally {
        if (signal.alive) setLoading(false);
      }
    },
    [rawCourseId]
  );

  useEffect(() => {
    const signal = { alive: true };
    void fetchCourse(signal);
    return () => { signal.alive = false; };
  }, [fetchCourse]);

  useEffect(() => {
    void trackPageView(pagePath);
    void trackFunnelStep('course_view', { course_id: rawCourseId, source, conversation_id: conversationId });
  }, [pagePath, rawCourseId, source, conversationId]);

  const title = safeStr(dbCourse?.title || payload.name);
  const provider = safeStr(dbCourse?.provider || payload.mentor);
  const price = safeStr(dbCourse?.price || payload.price);
  const duration = safeStr(dbCourse?.duration || payload.duration);
  const format = safeStr(dbCourse?.format || payload.learningType);
  const level = safeStr(dbCourse?.level);
  const language = safeStr(dbCourse?.language || payload.language);
  const topic = safeStr(dbCourse?.topic || payload.topic);
  const focus = safeStr(dbCourse?.focus);
  const type = safeStr(dbCourse?.type || payload.type);
  const audience = safeStr(dbCourse?.audience || payload.audience);
  const outcomes = safeStr(dbCourse?.outcomes || payload.outcomes);
  const deliverables = safeStr(dbCourse?.deliverables);
  const targetSkills = safeStr(dbCourse?.target_skills);
  const uniqueness = safeStr(dbCourse?.uniqueness);
  const hasVoucher = dbCourse?.has_voucher ?? false;
  const voucherText = safeStr(dbCourse?.voucher_text || dbCourse?.vouchers_for_kursi_users);

  const rawSummary = safeStr(dbCourse?.summary || payload.summary);
  const fallbackText = safeStr(dbCourse?.lightrag_doc || dbCourse?.relevant);
  const summary = rawSummary || (fallbackText.length > 30 ? truncate(fallbackText, 400) : '');
  const matchReason = safeStr((payload as any).matchReason);
  const externalUrl = getFirstUrl(dbCourse?.source_urls) || getFirstUrl((payload as any).source_url) || '';

  const metaTitle = buildMetaTitle(title, provider);
  const metaDesc = buildMetaDesc(title, provider, summary);
  const canonicalUrl = `${SITE_URL}/course/${encodeURIComponent(rawCourseId)}`;

  const learnItems = useMemo(() => {
    const parts = [deliverables, outcomes, targetSkills].filter(Boolean).join('\n');
    return splitItems(parts).slice(0, 10);
  }, [deliverables, outcomes, targetSkills]);

  const tagsToShow = useMemo(() => {
    const arr = [topic, format, level, language].filter((t) => isMeaningfulTag(t));
    return arr.slice(0, 6);
  }, [topic, format, level, language]);

  const [relatedDb, setRelatedDb] = useState<RelatedCourse[]>([]);
  const [relatedDbLoading, setRelatedDbLoading] = useState(false);

  const fetchRelatedFallback = useCallback(async () => {
    if (!rawCourseId) return;
    if (hasRelatedFromGate) return;
    const t = safeStr(topic);
    const f = safeStr(focus);
    if (!t && !f) return;

    setRelatedDbLoading(true);
    try {
      const q = supabase
        .from('processed_raw_courses')
        .select('course_id,source_url_key,title,provider,topic,format,level,language,summary,focus')
        .is('deleted_at', null)
        .order('id', { ascending: false })
        .limit(18);

      const orParts: string[] = [];
      if (t) orParts.push(`topic.ilike.%${t}%`);
      if (f) orParts.push(`focus.ilike.%${f}%`);

      const { data, error } = orParts.length ? await q.or(orParts.join(',')) : await q;
      if (error || !data) { setRelatedDb([]); return; }
      setRelatedDb(normalizeRelatedItems(data as any[], rawCourseId).slice(0, 6));
    } catch {
      setRelatedDb([]);
    } finally {
      setRelatedDbLoading(false);
    }
  }, [rawCourseId, hasRelatedFromGate, topic, focus]);

  useEffect(() => {
    if (!loading && title) void fetchRelatedFallback();
  }, [loading, title, fetchRelatedFallback]);

  const relatedCoursesFinal = hasRelatedFromGate ? relatedFromGate : relatedDb;

  const createPageSession = useCallback(async (): Promise<string> => {
    if (pageSessionPk) return pageSessionPk;
    if (!rawCourseId) return '';

    if (pendingSessionRef.current) return pendingSessionRef.current;

    const doCreate = async (): Promise<string> => {
      const session_id =
        safeStr((payload as any).session_id || (payload as any).sessionId) ||
        (conversationId ? `conv_${conversationId}` : '') ||
        pageSessionClientIdRef.current;

      try {
        const { data, error } = await supabase
          .from('course_page_sessions')
          .insert({
            session_id,
            user_id: null,
            course_id: rawCourseId,
            conversation_id: conversationId || null,
            referrer: source || null,
            utm_source: null,
            utm_medium: null,
            utm_campaign: null,
          })
          .select('id')
          .limit(1)
          .single();

        if (error) return '';
        const pk = safeStr((data as any)?.id);
        if (pk) setPageSessionPk(pk);
        return pk;
      } catch {
        return '';
      } finally {
        pendingSessionRef.current = null;
      }
    };

    pendingSessionRef.current = doCreate();
    return pendingSessionRef.current;
  }, [pageSessionPk, rawCourseId, payload, conversationId, source]);

  const logEvent = useCallback(
    async (event_name: string, extra?: { related_course_id?: string; position?: number; meta?: Record<string, any> }) => {
      const pk = pageSessionPk || (await createPageSession());
      if (!pk) return;
      try {
        await supabase.from('course_page_events').insert({
          session_pk: pk,
          event_name,
          course_id: rawCourseId,
          related_course_id: extra?.related_course_id || null,
          position: typeof extra?.position === 'number' ? extra.position : null,
          meta: extra?.meta || null,
        });
      } catch { /* ignore */ }
    },
    [pageSessionPk, createPageSession, rawCourseId]
  );

  useEffect(() => {
    if (!loading && rawCourseId && title) void createPageSession();
  }, [loading, rawCourseId, title, createPageSession]);

  useEffect(() => {
    if (!IS_WEB || typeof document === 'undefined' || !rawCourseId) return;
    const t = safeStr(title);
    if (!t) return;

    const pageUrl = `${SITE_URL}/course/${encodeURIComponent(rawCourseId)}`;
    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: t,
      description: safeStr(summary) || buildMetaDesc(t, safeStr(provider), ''),
      url: pageUrl,
      inLanguage: safeStr(language) || 'he',
      provider: { '@type': 'Organization', name: safeStr(provider) || 'Kursi', url: SITE_URL },
      ...(price && { offers: { '@type': 'Offer', price: price.replace(/[^\d.]/g, ''), priceCurrency: 'ILS' } }),
      ...(level && { educationalLevel: level }),
      ...(format && { courseMode: format }),
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'קורסים', item: `${SITE_URL}/courses` },
        { '@type': 'ListItem', position: 3, name: t, item: pageUrl },
      ],
    };

    const injected: HTMLScriptElement[] = [];
    [schema, breadcrumb].forEach((obj, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-kursi-schema', `course-proxy-${rawCourseId}-${i}`);
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
      injected.push(el);
    });

    return () => injected.forEach((el) => el.parentNode?.removeChild(el));
  }, [rawCourseId, title, provider, summary, price, level, language, format]);

  const openOfficialSiteNow = useCallback(() => {
    if (!externalUrl) return;
    void logEvent('official_site_click', { meta: { via: 'direct' } });
    if (IS_WEB && typeof window !== 'undefined') {
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(externalUrl).catch(() => Alert.alert('שגיאה', 'לא ניתן לפתוח את הקישור'));
    }
  }, [externalUrl, logEvent]);

  const openModal = useCallback((mode: 'lead' | 'official' | 'newsletter') => {
    setModalMode(mode);
    setModalVisible(true);
    void logEvent(
      mode === 'lead' ? 'lead_gate_open' :
      mode === 'official' ? 'official_gate_open' : 'newsletter_gate_open',
      { meta: { mode } }
    );
  }, [logEvent]);

  const handleLeadCTA = useCallback((position: 'hero' | 'bottom') => {
    if (!title && !provider) return;
    void trackButtonClick(`cta_lead_${position}`, pagePath, { course_id: rawCourseId, course_name: title, position });
    void trackFunnelStep('lead_modal_open', { course_id: rawCourseId, position });
    void logEvent('lead_details_click', { meta: { position } });
    openModal('lead');
  }, [title, provider, pagePath, rawCourseId, logEvent, openModal]);

  // ✅ FIX: now opens 'official' modal instead of navigating directly
  const handleExternalCTA = useCallback((position: 'hero' | 'bottom') => {
    if (!externalUrl) return;
    void trackButtonClick(`cta_external_site_${position}`, pagePath, { course_id: rawCourseId, course_name: title, position });
    void trackFunnelStep('course_cta_click', { course_id: rawCourseId, position });
    void logEvent('official_site_intent', { meta: { position } });
    openModal('official');
  }, [externalUrl, pagePath, rawCourseId, title, logEvent, openModal]);

  const handleSendLinkCTA = useCallback((position: 'hero' | 'bottom') => {
    if (!externalUrl) return;
    void trackButtonClick(`cta_send_link_${position}`, pagePath, { course_id: rawCourseId, course_name: title, position });
    void trackFunnelStep('official_link_request_open', { course_id: rawCourseId, position });
    void logEvent('official_link_request_intent', { meta: { position } });
    openModal('official');
  }, [externalUrl, pagePath, rawCourseId, title, logEvent, openModal]);

  const handleNewsletterCTA = useCallback(() => {
    void trackButtonClick('cta_newsletter_open', pagePath, { course_id: rawCourseId });
    void trackFunnelStep('newsletter_open', { course_id: rawCourseId });
    void logEvent('newsletter_intent');
    openModal('newsletter');
  }, [pagePath, rawCourseId, logEvent, openModal]);

  const handleLogoClick = useCallback(() => {
    void trackButtonClick('logo_click', pagePath, { course_id: rawCourseId });
    void logEvent('logo_click', { meta: { from: pagePath } });
    router.push('/');
  }, [pagePath, rawCourseId, router, logEvent]);

  const handleRelatedClick = useCallback((c: RelatedCourse, position: number) => {
    void trackButtonClick('related_course_click', pagePath, {
      course_id: rawCourseId,
      related_course_id: c.course_id,
      position,
    });
    void logEvent('related_course_click', {
      related_course_id: c.course_id,
      position,
      meta: { related_title: c.title, related_provider: c.provider },
    });

    router.push({
      pathname: '/course/[courseId]',
      params: {
        courseId: c.course_id,
        source: hasRelatedFromGate ? 'related_gate' : 'related_db',
        conversationId: conversationId || undefined,
        payload: params.payload,
        relatedCourses: encodeURIComponent(JSON.stringify([...relatedFromParams, ...relatedFromPayload])),
      } as any,
    });
  }, [pagePath, rawCourseId, router, conversationId, logEvent, params.payload, hasRelatedFromGate, relatedFromParams, relatedFromPayload]);

  const courseForModal: Course | null =
    title || provider
      ? {
          id: rawCourseId,
          name: title || 'קורס',
          description: summary,
          mentor: provider,
          price,
          learningType: format,
          duration,
          audience,
          outcomes,
          deliverables,
        }
      : null;

  const gateCourseForModal: Course = {
    id: rawCourseId || 'unknown',
    name: title || 'קורס',
    description: summary,
    mentor: provider,
    price,
    learningType: format,
    duration,
    audience,
    outcomes,
    deliverables,
  };

  if (!gateLoaded) {
    return (
      <View style={s.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={s.loadingText}>טוען…</Text>
      </View>
    );
  }

  if (!gateUnlocked) {
    return (
      <View style={s.loadingContainer}>
        <BookCallModal
          visible
          course={gateCourseForModal}
          mode="official"
          hardGate
          title="לפני הכניסה לדף הקורס"
          subtitle="כדי לצפות בפרטי הקורס, צריך להשאיר אימייל תקין."
          primaryLabel="המשך עם אימייל"
          onClose={() => {}}
          onSubmitted={async () => {
            try {
              await AsyncStorage.setItem(getGateAccessKey(rawCourseId), '1');
            } catch {}
            setGateUnlocked(true);
          }}
          source="course_page_gate"
          conversationId={conversationId || undefined}
          sessionId={safeStr((payload as any).session_id || (payload as any).sessionId) || undefined}
          notesPreset={matchReason ? `למה הותאם: ${matchReason}` : undefined}
        />
      </View>
    );
  }

  if (loading) {
    return (
      <View style={s.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={s.loadingText}>טוען פרטי קורס…</Text>
      </View>
    );
  }

  if (fetchError) {
    return (
      <View style={s.notFoundWrap}>
        <Text style={s.notFoundTitle}>לא הצלחנו לטעון את הקורס</Text>
        <Text style={s.notFoundBody}>{fetchError}</Text>
        <Pressable
          style={s.retryBtn}
          onPress={() => {
            const signal = { alive: true };
            void fetchCourse(signal);
          }}
          accessibilityRole="button"
          accessibilityLabel="נסה שוב"
        >
          <RefreshCcw size={16} color="#fff" />
          <Text style={s.retryBtnText}>נסה שוב</Text>
        </Pressable>
      </View>
    );
  }

  if (!title) {
    return (
      <View style={s.notFoundWrap}>
        <Text style={s.notFoundTitle}>הקורס לא נמצא</Text>
        <Text style={s.notFoundBody}>הלינק אולי ישן או הקורס הוסר.</Text>
      </View>
    );
  }

  const onMainScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y;
    const layout = relatedSectionLayoutRef.current;

    if (!relatedSectionViewedRef.current && layout && y + 700 >= layout.y) {
      relatedSectionViewedRef.current = true;
      void logEvent('related_section_view', {
        meta: {
          related_count: relatedCoursesFinal.length,
          source: hasRelatedFromGate ? 'gate' : 'db_fallback',
          impressions: relatedCoursesFinal.map((c, i) => ({
            course_id: c.course_id,
            title: c.title,
            provider: c.provider,
            position: i + 1,
          })),
        },
      });
    }
  };

  const onRelatedScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const x = e.nativeEvent.contentOffset.x;
    const now = Date.now();
    if (now - lastRelatedScrollLogTsRef.current < 1200) return;
    lastRelatedScrollLogTsRef.current = now;
    void logEvent('related_scroll_depth', {
      meta: { scrollX: Math.round(x), source: hasRelatedFromGate ? 'gate' : 'db_fallback' },
    });
  };

  return (
    <>
      {IS_WEB && (
        <Head>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDesc} />
          <link rel="canonical" href={canonicalUrl} />
          <link rel="alternate" hrefLang="he" href={canonicalUrl} />
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDesc} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="twitter:description" content={metaDesc} />
        </Head>
      )}

      <ScrollView
        style={s.scroll}
        contentContainerStyle={s.container}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={onMainScroll}
      >
        <Animated.View style={entryStyle}>
          {/* Header */}
          <View style={s.topHeader}>
            <Pressable
              onPress={handleLogoClick}
              accessibilityRole="button"
              accessibilityLabel="לוגו קורסי - חזרה לדף הבית"
              style={s.logoBtn}
            >
              <Image source={KURSI_LOGO} style={s.logoImg} />
            </Pressable>
          </View>

          <View style={s.headerLine} />

          {/* HERO */}
          <View style={s.hero}>
            {tagsToShow.length > 0 ? (
              <View style={s.tagsRow}>
                {tagsToShow.map((t, i) => (
                  <Tag key={`${t}_${i}`} text={t} />
                ))}
              </View>
            ) : null}

            <Text style={s.heroTitle}>{title}</Text>
            {provider ? <Text style={s.heroProvider}>{provider}</Text> : null}
            {summary ? <Text style={s.heroSummary} numberOfLines={6}>{summary}</Text> : null}

            <View style={s.ctaStack}>
              <Pressable
                style={s.primaryCTA}
                onPress={() => handleLeadCTA('hero')}
                accessibilityRole="button"
                accessibilityLabel="קבלו פרטים ישירות אליכם"
              >
                <Phone size={17} color="#fff" />
                <Text style={s.primaryCTAText}>קבלו פרטים ישירות אליכם</Text>
              </Pressable>

              {externalUrl ? (
                <Pressable
                  style={s.secondaryCTA}
                  onPress={() => handleExternalCTA('hero')}
                  accessibilityRole="button"
                  accessibilityLabel="מעבר לאתר הרשמי"
                >
                  <ExternalLink size={15} color={theme.colors.primary} />
                  <Text style={s.secondaryCTAText}>מעבר לאתר הרשמי</Text>
                </Pressable>
              ) : null}

              {externalUrl ? (
                <Pressable
                  style={s.tertiaryCTA}
                  onPress={() => handleSendLinkCTA('hero')}
                  accessibilityRole="button"
                  accessibilityLabel="שלחו לי את הלינק + הטבות"
                >
                  <Mail size={16} color={UI.muted} />
                  <Text style={s.tertiaryCTAText}>שלחו לי את הלינק + הטבות</Text>
                </Pressable>
              ) : null}
            </View>
          </View>

          <View style={s.flowDivider} />

          {/* Trust badge */}
          <View style={s.trustBadge}>
            <Sparkles size={14} color={theme.colors.primary} />
            <Text style={s.trustText}>הותאם עבורך ע״י AI של קורסי</Text>
          </View>

          {/* Sections */}
          <Section index={0}>
            {(price || duration || format || level || language || type) ? (
              <>
                <Text style={s.sectionTitle}>פרטי הקורס</Text>
                <View style={s.factsGrid}>
                  <FactItem icon={<DollarSign size={16} color={theme.colors.primary} />} label="מחיר" value={price} />
                  <FactItem icon={<Clock size={16} color={theme.colors.primary} />} label="משך" value={duration} />
                  <FactItem icon={<Monitor size={16} color={theme.colors.primary} />} label="פורמט" value={format} />
                  <FactItem icon={<Award size={16} color={theme.colors.primary} />} label="רמה" value={level} />
                  <FactItem icon={<Globe size={16} color={theme.colors.primary} />} label="שפה" value={language} />
                  <FactItem icon={<BookOpen size={16} color={theme.colors.primary} />} label="סוג" value={type} />
                </View>
              </>
            ) : (
              <Text style={s.mutedText}>אין כרגע פרטים מהירים להצגה.</Text>
            )}
          </Section>

          {matchReason && matchReason.length > 5 ? (
            <Section index={1}>
              <View style={s.sectionTitleRow}>
                <Zap size={18} color={theme.colors.primary} />
                <Text style={s.sectionTitleInline}>למה זה מתאים למה שחיפשת</Text>
              </View>
              <Text style={s.bodyText}>{matchReason}</Text>
            </Section>
          ) : null}

          {learnItems.length > 0 ? (
            <Section index={2}>
              <Text style={s.sectionTitle}>מה תלמד ומה תדע לעשות</Text>
              <View style={s.learnList}>
                {learnItems.map((item, i) => (
                  <View key={i} style={s.learnRow}>
                    <View style={s.learnIcon}><CheckCircle size={15} color="#16A34A" /></View>
                    <Text style={s.learnText}>{item}</Text>
                  </View>
                ))}
              </View>
            </Section>
          ) : null}

          {uniqueness && uniqueness.length > 5 ? (
            <Section index={3}>
              <Text style={s.sectionTitle}>מה מיוחד בקורס הזה</Text>
              <Text style={s.bodyText}>{uniqueness}</Text>
            </Section>
          ) : null}

          {hasVoucher && voucherText ? (
            <Section index={4}>
              <View style={s.sectionTitleRow}>
                <Gift size={18} color="#F59E0B" />
                <Text style={[s.sectionTitleInline, s.voucherTitle]}>הטבה למשתמשי קורסי</Text>
              </View>
              <Text style={s.voucherBody}>{voucherText}</Text>
            </Section>
          ) : null}

          <Section index={5}>
            <View style={s.inlineCardTop}>
              <Sparkles size={16} color={theme.colors.primary} />
              <Text style={s.inlineCardTitle}>רוצים לקבל קורסים והטבות שמתאימים לכם?</Text>
            </View>
            <Text style={s.inlineCardBody}>השאירו מייל וטלפון ונשלח כשיש משהו רלוונטי.</Text>
            <Pressable
              style={s.inlinePrimaryBtn}
              onPress={handleNewsletterCTA}
              accessibilityRole="button"
              accessibilityLabel="הצטרפות לעדכונים"
            >
              <Text style={s.inlinePrimaryText}>הצטרפות לעדכונים</Text>
            </Pressable>
          </Section>

          <Section index={6}>
            <View
              onLayout={(e) => {
                const { y, height } = e.nativeEvent.layout;
                relatedSectionLayoutRef.current = { y, h: height };
              }}
            >
              <View style={s.sectionTitleRow}>
                <Sparkles size={16} color={theme.colors.primary} />
                <Text style={s.sectionTitleInline}>עוד קורסים שיכולים להתאים לך</Text>
              </View>

              {relatedDbLoading && relatedCoursesFinal.length === 0 ? (
                <View style={s.relatedLoadingRow}>
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                  <Text style={s.mutedText}>מחפש עוד קורסים…</Text>
                </View>
              ) : relatedCoursesFinal.length > 0 ? (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={s.relatedRow}
                  onScroll={onRelatedScroll}
                  scrollEventThrottle={16}
                >
                  {relatedCoursesFinal.map((c, i) => (
                    <RelatedCard
                      key={`${c.course_id}_${i}`}
                      c={c}
                      index={i}
                      onPress={() => handleRelatedClick(c, i + 1)}
                    />
                  ))}
                </ScrollView>
              ) : (
                <Text style={s.mutedText}>אין כרגע קורסים נוספים להציג.</Text>
              )}
            </View>
          </Section>

          <Section index={7}>
            <View style={s.bottomCTASection}>
              <Pressable
                style={s.primaryCTA}
                onPress={() => handleLeadCTA('bottom')}
                accessibilityRole="button"
                accessibilityLabel="קבלו פרטים ישירות אליכם"
              >
                <MessageCircle size={17} color="#fff" />
                <Text style={s.primaryCTAText}>קבלו פרטים ישירות אליכם</Text>
              </Pressable>

              {externalUrl ? (
                <Pressable
                  style={s.secondaryCTA}
                  onPress={() => handleExternalCTA('bottom')}
                  accessibilityRole="button"
                  accessibilityLabel="מעבר לאתר הרשמי"
                >
                  <ExternalLink size={15} color={theme.colors.primary} />
                  <Text style={s.secondaryCTAText}>מעבר לאתר הרשמי</Text>
                </Pressable>
              ) : null}

              {externalUrl ? (
                <Pressable
                  style={s.tertiaryCTA}
                  onPress={() => handleSendLinkCTA('bottom')}
                  accessibilityRole="button"
                  accessibilityLabel="שלחו לי את הלינק + הטבות"
                >
                  <Mail size={16} color={UI.muted} />
                  <Text style={s.tertiaryCTAText}>שלחו לי את הלינק + הטבות</Text>
                </Pressable>
              ) : null}
            </View>
          </Section>

          <View style={{ height: 60 }} />
        </Animated.View>
      </ScrollView>

      <BookCallModal
        visible={modalVisible}
        course={courseForModal}
        mode={modalMode}
        onClose={() => setModalVisible(false)}
        onSkip={modalMode === 'official' ? openOfficialSiteNow : undefined}
        source="course_proxy"
        conversationId={conversationId || undefined}
        sessionId={safeStr((payload as any).session_id || (payload as any).sessionId) || undefined}
        notesPreset={matchReason ? `למה הותאם: ${matchReason}` : undefined}
      />
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const MAX_WIDTH = 720;

const s = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    backgroundColor: UI.bg,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: { fontFamily: theme.fonts.regular, fontSize: 14, color: UI.muted },

  notFoundWrap: {
    flex: 1,
    backgroundColor: UI.bg,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    gap: 12,
  },
  notFoundTitle: { fontFamily: theme.fonts.bold, fontSize: 20, color: UI.text, textAlign: 'center' },
  notFoundBody: { fontFamily: theme.fonts.regular, fontSize: 14, color: UI.textLight, textAlign: 'center' },
  retryBtn: {
    marginTop: 6,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: 12,
    paddingHorizontal: 16,
  } as any,
  retryBtnText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },

  scroll: { flex: 1, backgroundColor: UI.bg },
  container: {
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 40,
    width: '100%',
    maxWidth: IS_WEB ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  } as any,

  topHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: 6,
    marginBottom: 6,
  } as any,

  logoBtn: {
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: theme.borderRadius.md,
  } as any,
  logoImg: {
    width: IS_WEB ? 92 : 82,
    height: IS_WEB ? 92 : 82,
    resizeMode: 'contain',
  },

  headerLine: {
    height: 1,
    backgroundColor: UI.border,
    marginBottom: 10,
  },

  hero: { paddingBottom: 10 },

  tagsRow: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
    justifyContent: 'flex-end',
    alignSelf: 'stretch',
  } as any,

  tag: {
    borderWidth: 1,
    borderColor: UI.chipBorder,
    backgroundColor: UI.chipBg,
    borderRadius: theme.borderRadius.full,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  tagText: { fontFamily: theme.fonts.medium, fontSize: 12, color: theme.colors.primary },

  heroTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: IS_WEB ? 30 : 22,
    color: UI.text,
    textAlign: 'right',
    lineHeight: IS_WEB ? 40 : 30,
    marginBottom: 6,
  },
  heroProvider: {
    fontFamily: theme.fonts.medium,
    fontSize: 15,
    color: theme.colors.primary,
    textAlign: 'right',
    marginBottom: 10,
  },
  heroSummary: {
    fontFamily: theme.fonts.regular,
    fontSize: 15,
    color: UI.textLight,
    textAlign: 'right',
    lineHeight: 24,
    marginBottom: 16,
  },

  ctaStack: { gap: 10 },

  primaryCTA: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: 15,
    paddingHorizontal: 20,
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 2,
  } as any,
  primaryCTAText: { fontFamily: theme.fonts.bold, fontSize: 15, color: '#fff', textAlign: 'center' },

  secondaryCTA: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: 13,
    paddingHorizontal: 20,
    backgroundColor: UI.chipBg,
  } as any,
  secondaryCTAText: { fontFamily: theme.fonts.bold, fontSize: 14, color: theme.colors.primary, textAlign: 'center' },

  tertiaryCTA: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: UI.border,
    borderRadius: theme.borderRadius.md,
    paddingVertical: 12,
    paddingHorizontal: 18,
    backgroundColor: UI.bg,
  } as any,
  tertiaryCTAText: { fontFamily: theme.fonts.semiBold, fontSize: 13, color: UI.muted, textAlign: 'center' },

  flowDivider: {
    height: 1,
    backgroundColor: UI.border,
    marginTop: 6,
    marginBottom: 10,
  },

  trustBadge: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: UI.chipBg,
    borderRadius: theme.borderRadius.full,
    borderWidth: 1,
    borderColor: UI.chipBorder,
    alignSelf: 'center',
    marginTop: 4,
    marginBottom: 12,
  } as any,
  trustText: { fontFamily: theme.fonts.medium, fontSize: 12, color: theme.colors.primary, textAlign: 'center' },

  sectionCard: {
    borderWidth: 1,
    borderColor: UI.border,
    borderRadius: theme.borderRadius.lg,
    padding: 14,
    marginBottom: 12,
  },

  sectionTitleRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  } as any,
  sectionTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: IS_WEB ? 18 : 16,
    color: UI.text,
    textAlign: 'right',
    marginBottom: 12,
  },
  sectionTitleInline: {
    fontFamily: theme.fonts.bold,
    fontSize: IS_WEB ? 18 : 16,
    color: UI.text,
    textAlign: 'right',
    flex: 1,
  },

  factsGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 10 } as any,
  factItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    minWidth: IS_WEB ? 200 : 140,
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: theme.borderRadius.md,
    padding: 10,
    borderWidth: 1,
    borderColor: UI.border,
  } as any,
  factTexts: { flex: 1, alignItems: 'flex-end' },
  factLabel: { fontFamily: theme.fonts.regular, fontSize: 11, color: UI.muted, textAlign: 'right' },
  factValue: { fontFamily: theme.fonts.semiBold, fontSize: 13, color: UI.text, textAlign: 'right', marginTop: 2 },

  bodyText: { fontFamily: theme.fonts.regular, fontSize: 14, color: UI.textLight, textAlign: 'right', lineHeight: 22 },

  learnList: { gap: 10 },
  learnRow: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 10 } as any,
  learnIcon: { marginTop: 2, flexShrink: 0 },
  learnText: { fontFamily: theme.fonts.regular, fontSize: 14, color: UI.text, textAlign: 'right', lineHeight: 22, flex: 1 },

  voucherTitle: { color: '#B45309' },
  voucherBody: { fontFamily: theme.fonts.regular, fontSize: 14, color: UI.text, textAlign: 'right', lineHeight: 22 },

  inlineCardTop: { flexDirection: 'row-reverse', alignItems: 'center', gap: 8, marginBottom: 6 } as any,
  inlineCardTitle: { fontFamily: theme.fonts.bold, fontSize: 14, color: UI.text, textAlign: 'right', flex: 1 },
  inlineCardBody: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: UI.textLight,
    textAlign: 'right',
    lineHeight: 18,
    marginBottom: 10,
  },
  inlinePrimaryBtn: {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    paddingVertical: 12,
    alignItems: 'center',
  },
  inlinePrimaryText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },

  relatedRow: { gap: 10, paddingVertical: 4 } as any,
  relatedCard: {
    width: IS_WEB ? 320 : 270,
    padding: 14,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: UI.border,
  } as any,
  relatedCardTop: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 6,
  } as any,
  relatedTitle: { fontFamily: theme.fonts.bold, fontSize: 15, color: UI.text, textAlign: 'right', flex: 1, lineHeight: 20 },
  relatedProvider: { fontFamily: theme.fonts.medium, fontSize: 13, color: theme.colors.primary, textAlign: 'right', marginBottom: 4 },
  relatedMeta: { fontFamily: theme.fonts.regular, fontSize: 12, color: UI.muted, textAlign: 'right', marginBottom: 8 },
  relatedSummary: { fontFamily: theme.fonts.regular, fontSize: 13, color: UI.textLight, textAlign: 'right', lineHeight: 20 },
  mutedText: { fontFamily: theme.fonts.regular, fontSize: 13, color: UI.muted, textAlign: 'right', paddingTop: 4 },
  relatedLoadingRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 10, paddingTop: 6 } as any,

  bottomCTASection: { gap: 10 },
});